import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Film, Music, Trash2 } from 'lucide-react';
import { mediaApi, displayStatusFromMediaAsset } from '@/api/mediaWrapper';
import type { MediaAssetResponse, MediaTagResponse } from 'we-tree-api-client/v2';
import MediaTagModal from '@/components/media/MediaTagModal';
import { getMediaFileCategoryFromMimeType } from '@/utils/mediaUpload';

import './media-card.css';

export type MediaCardThumbSize = '40x40' | '200x200';

export interface MediaCardProps {
  familyId: string;
  asset: MediaAssetResponse;
  /** Thumbnail size for the card image. Use 40x40 for dense grids (e.g. Choose Media gallery). */
  thumbSize?: MediaCardThumbSize;
  /** Masonry layout: preserve natural aspect ratio with softer contain cropping */
  layout?: 'grid' | 'masonry';
  /** Optional: navigate to person profile when refs support it */
  onSelect?: (mediaAssetId: string) => void;
  /** Tags available to assign; when provided, enables tagging UI */
  availableTags?: MediaTagResponse[];
  /** Called when tags are added or removed */
  onTagsChange?: () => void;
  /** Whether the user can edit tags on this media (based on role). When false, tag button is hidden. */
  canEdit?: boolean;
  /** Whether the user can delete this media (media:delete). When false, delete button is hidden. */
  canDelete?: boolean;
  /** Called after the user confirms delete (parent runs store action + refresh). */
  onDelete?: (mediaAssetId: string) => void;
  /** True while this asset is being deleted. */
  isDeleting?: boolean;
}

const MediaCard: React.FC<MediaCardProps> = ({
  familyId,
  asset,
  thumbSize = '200x200',
  layout = 'grid',
  onSelect,
  availableTags = [],
  onTagsChange,
  canEdit = false,
  canDelete = false,
  onDelete,
  isDeleting = false
}) => {
  const { t } = useTranslation('forms');
  const { t: tScreens } = useTranslation('screens');
  const { t: tCommon } = useTranslation('common');
  const [displayUrl, setDisplayUrl] = useState<string | null>(null);
  const [displayStatus, setDisplayStatus] = useState<'safe' | 'processing' | 'unavailable' | null>(null);
  const [statusDetail, setStatusDetail] = useState<string | null>(null);
  const [showTagModal, setShowTagModal] = useState(false);

  const category = getMediaFileCategoryFromMimeType(asset.mimeType);
  const isImage = category === 'photo';
  const isVideo = category === 'video';

  useEffect(() => {
    let cancelled = false;

    if (isVideo) {
      const derived = displayStatusFromMediaAsset(asset);
      setDisplayStatus(derived.displayStatus);
      setStatusDetail(derived.message ?? null);
      setDisplayUrl(null);
      if (derived.displayStatus !== 'safe') {
        return;
      }
      mediaApi.getVideoPosterUrl(familyId, asset.id).then((url) => {
        if (cancelled) return;
        setDisplayUrl(url);
      });
      return () => {
        cancelled = true;
      };
    }

    if (!isImage) {
      setDisplayUrl(null);
      setDisplayStatus('safe');
      setStatusDetail(null);
      return;
    }

    mediaApi.getMediaDisplay(familyId, asset.id, thumbSize).then((result) => {
      if (cancelled) return;
      if (result.success && result.data) {
        setDisplayUrl(result.data.url);
        setDisplayStatus(result.data.displayStatus);
        setStatusDetail(result.data.message ?? null);
      } else {
        setDisplayStatus('unavailable');
        setStatusDetail(null);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [familyId, asset, thumbSize, isImage, isVideo]);

  const showThumb = displayStatus === 'safe' && Boolean(displayUrl) && (isImage || isVideo);
  const isMasonry = layout === 'masonry' && thumbSize !== '40x40';
  // Only images use natural dimensions; video posters / audio placeholders need a stable card size.
  const naturalAspect =
    isImage && asset.width && asset.height && asset.width > 0 && asset.height > 0
      ? `${asset.width} / ${asset.height}`
      : undefined;
  const statusMessage =
    displayStatus === 'processing'
      ? statusDetail || (isVideo ? tScreens('mediaViewer.processingVideo') : tScreens('mediaViewer.processing'))
      : displayStatus === 'unavailable'
        ? statusDetail || (isVideo ? tScreens('mediaViewer.videoUnavailable') : tScreens('mediaViewer.unavailable'))
        : null;

  const placeholderLabel = isVideo
    ? tScreens('mediaViewer.videoLabel')
    : category === 'audio'
      ? tScreens('mediaViewer.audioLabel')
      : statusMessage || tScreens('mediaViewer.fileLabel');

  const canOpen = Boolean(onSelect) && displayStatus === 'safe';

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isDeleting || !onDelete) return;
    const label = asset.originalFilename?.trim() || t('actions.thisFile');
    if (!window.confirm(t('confirm.deleteMedia', { label }))) return;
    onDelete(asset.id);
  };

  return (
    <div
      className={`media-card${thumbSize === '40x40' ? ' media-card--small' : ''}${
        isMasonry ? ' media-card--masonry' : ''
      }${isVideo ? ' media-card--video' : ''}${category === 'audio' ? ' media-card--audio' : ''}${
        isDeleting ? ' media-card--deleting' : ''
      }`}
      role={canOpen ? 'button' : undefined}
      tabIndex={canOpen ? 0 : undefined}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('.media-card__actions')) return;
        if (!canOpen) return;
        onSelect?.(asset.id);
      }}
      onKeyDown={
        canOpen
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelect?.(asset.id);
              }
            }
          : undefined
      }
    >
      <div
        className="media-card__thumb"
        style={isMasonry && naturalAspect ? { aspectRatio: naturalAspect } : undefined}
      >
        {showThumb ? (
          <img src={displayUrl!} alt="" className="media-card__img" loading="lazy" />
        ) : (
          <div
            className={`media-card__placeholder${
              isVideo || category === 'audio' ? ' media-card__placeholder--media' : ''
            }`}
          >
            {statusMessage ? (
              statusMessage
            ) : isVideo ? (
              <>
                <Film className="media-card__placeholder-icon" aria-hidden strokeWidth={1.75} />
                <span className="media-card__placeholder-label">{placeholderLabel}</span>
              </>
            ) : category === 'audio' ? (
              <>
                <Music className="media-card__placeholder-icon" aria-hidden strokeWidth={1.75} />
                <span className="media-card__placeholder-label">{placeholderLabel}</span>
              </>
            ) : (
              placeholderLabel
            )}
          </div>
        )}
        {(canEdit || canDelete) && (
          <div className="media-card__actions">
            {canDelete && (
              <button
                type="button"
                className="media-card__action media-card__action--danger"
                onClick={handleDeleteClick}
                disabled={isDeleting}
                aria-label={isDeleting ? t('actions.deleting') : t('actions.deleteMedia')}
                title={isDeleting ? t('actions.deleting') : tCommon('delete')}
              >
                <Trash2 width={14} height={14} aria-hidden strokeWidth={2} />
              </button>
            )}
            {canEdit && (
              <button
                type="button"
                className="media-card__action"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowTagModal(true);
                }}
                aria-label={t('actions.manageTags')}
                aria-haspopup="dialog"
                aria-expanded={showTagModal}
                title={t('actions.tags')}
              >
                🏷️
              </button>
            )}
          </div>
        )}
      </div>

      {showTagModal && (
        <MediaTagModal
          familyId={familyId}
          asset={asset}
          availableTags={availableTags}
          isOpen={showTagModal}
          onClose={() => setShowTagModal(false)}
          onTagsChange={() => {
            onTagsChange?.();
          }}
        />
      )}
    </div>
  );
};

export default MediaCard;
